// src/components/aiondemand/ArchivedJobsSection.jsx

import { useEffect } from 'react';
import { History, Loader2, CheckCircle2, AlertCircle, Archive } from 'lucide-react';

import { useAIOnDemandStore } from '../../stores/useAIOnDemandStore';
import { useProjectStore } from '../../stores/useProjectStore';
import { getActionById } from '../../actions/registry.js';

export default function ArchivedJobsSection({ onSelectJob }) {
  const projectId = useProjectStore((s) => s.activeProjectId);
  const archivedJobs = useAIOnDemandStore((s) => s.archivedJobs);
  const archivedJobsLoading = useAIOnDemandStore((s) => s.archivedJobsLoading);
  const loadArchivedJobs = useAIOnDemandStore((s) => s.loadArchivedJobs);

  useEffect(() => {
    if (projectId) loadArchivedJobs(projectId);
  }, [projectId, loadArchivedJobs]);

  return (
    <div className="border-b border-slate-800 last:border-b-0">
      <div className="px-3 py-2 flex items-center gap-1.5">
        <History size={11} className="text-fg-secondary" />
        <h3 className="text-[10px] font-semibold tracking-[0.14em] font-mono uppercase text-slate-400">
          History
        </h3>
        {archivedJobs.length > 0 && (
          <span className="text-[9px] text-fg-secondary">({archivedJobs.length})</span>
        )}
      </div>

      <div className="pb-3">
        {archivedJobsLoading && archivedJobs.length === 0 ? (
          <p className="px-3 text-[10px] text-fg-secondary italic">Loading…</p>
        ) : archivedJobs.length === 0 ? (
          <p className="px-3 text-[10px] text-fg-secondary italic leading-relaxed">
            No archived jobs.
          </p>
        ) : (
          <div className="flex flex-col gap-1 px-2">
            {archivedJobs.slice(0, 20).map((job) => (
              <ArchivedJobCard
                key={job.id}
                job={job}
                onClick={() => onSelectJob(job.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ArchivedJobCard({ job, onClick }) {
  const action = getActionById(job.task_key) ?? null;
  const actionName = action?.name ?? job.task_key;

  return (
    <button
      onClick={onClick}
      className="group flex items-center gap-2 rounded-md px-2.5 py-2
                 bg-bg border border-slate-800 hover:border-slate-700
                 hover:bg-bg-soft transition text-left opacity-70 hover:opacity-100"
    >
      <StatusIcon status={job.status} />
      <div className="min-w-0 flex-1">
        <div className="text-xs font-medium text-slate-300 truncate">
          {actionName}
        </div>
        <div className="text-[10px] text-fg-secondary truncate">
          {summarizeArchivedJob(job)}
        </div>
      </div>
      <Archive size={10} className="text-fg-secondary shrink-0" />
    </button>
  );
}

function StatusIcon({ status }) {
  if (status === 'completed') return <CheckCircle2 size={12} className="text-fg-secondary shrink-0" />;
  if (status === 'failed') return <AlertCircle size={12} className="text-fg-secondary shrink-0" />;
  return <div className="w-3 h-3 rounded-full bg-slate-700 shrink-0" />;
}

function summarizeArchivedJob(job) {
  const parts = [];
  parts.push(job.aoi_id ? 'AOI' : 'Viewport');
  if (job.archived_at) {
    parts.push(`archived ${formatRelative(job.archived_at)}`);
  }
  return parts.join(' · ');
}

function formatRelative(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}